import Vue from 'vue';
import livingAddressService from '@/api/living-address-service';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { updateArrayItem, removeArrayItem } from '@/util/array';
import { mapErrorsToInputs } from '@/util/forms';

const getDefaultLivingAddressFormItem = () => ({
  until_now: false,
  is_visible: true,
});

export const getDefaultLivingAddressFilterParams = () => ({});

const state = {
  livingAddresses: null,
  livingAddressPagination: {},
  editedLivingAddress: {},
  livingAddressValidationErrors: {},
  livingAddressFilterParams: getDefaultLivingAddressFilterParams(),
  newLivingAddress: getDefaultLivingAddressFormItem(),
};

const getters = {};

const mutations = {
  SET_LIVING_ADDRESSES(state, { data, current_page, per_page, total }) {
    state.livingAddresses = data;
    state.livingAddressPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_LIVING_ADDRESS_FILTER_PARAMS(state, params) {
    state.livingAddressFilterParams = params;
  },

  SET_EDITED_LIVING_ADDRESS(state, livingAddress) {
    state.livingAddressValidationErrors = {};
    state.editedLivingAddress = JSON.parse(JSON.stringify(livingAddress));
  },

  STORE_LIVING_ADDRESS(state, livingAddress) {
    state.livingAddresses = [...state.livingAddresses, livingAddress];
    state.livingAddressPagination.total += 1;
    state.livingAddressValidationErrors = {};
    state.newLivingAddress = getDefaultLivingAddressFormItem();
  },

  UPDATE_LIVING_ADDRESS(state, livingAddress) {
    state.livingAddresses = updateArrayItem(state.livingAddresses, livingAddress);
  },

  DELETE_LIVING_ADDRESS(state, livingAddress) {
    state.livingAddresses = removeArrayItem(state.livingAddresses, livingAddress);
    state.livingAddressPagination.total -= 1;
  },

  SET_LIVING_ADDRESS_VALIDATION_ERRORS(state, livingAddressValidationErrors) {
    state.livingAddressValidationErrors = livingAddressValidationErrors;
  },

  RESET_LIVING_ADDRESS_VALIDATION_ERRORS(state) {
    state.livingAddressValidationErrors = {};
  },

  CLEAR_LIVING_ADDRESS_VALIDATION_ERRORS(state, field) {
    Vue.delete(state.livingAddressValidationErrors, field);
  },
};

const actions = {
  fetchLivingAddresses({ commit }, params) {
    commit('SET_LIVING_ADDRESS_FILTER_PARAMS', params);
    return livingAddressService.getPage(params).then((res) => {
      commit('SET_LIVING_ADDRESSES', res.data);
      if (params.page !== state.livingAddressFilterParams.page) {
        window.scrollTo(0, 0);
      }
      return res.data;
    });
  },

  storeLivingAddress({ commit }, livingAddress) {
    return livingAddressService
      .create(livingAddress)
      .then((res) => {
        commit('STORE_LIVING_ADDRESS', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('living_address_created'));
        return res.data;
      })
      .catch((err) => {
        commit('SET_LIVING_ADDRESS_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  editLivingAddress({ state, commit }, livingAddressId) {
    const livingAddress = state.livingAddresses?.find((c) => c.id === livingAddressId);
    if (livingAddress) {
      commit('SET_EDITED_LIVING_ADDRESS', livingAddress);
      return Promise.resolve(livingAddress);
    }
    return livingAddressService.getById(livingAddressId).then((res) => {
      commit('SET_EDITED_LIVING_ADDRESS', res.data);
      return res.data;
    });
  },

  updateLivingAddress({ commit }, livingAddress) {
    return livingAddressService
      .update(livingAddress)
      .then((res) => {
        commit('UPDATE_LIVING_ADDRESS', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('living_address_updated'));
        return res.data;
      })
      .catch((err) => {
        commit('SET_LIVING_ADDRESS_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  clearLivingAddressValidationErrors({ commit }, field) {
    commit('CLEAR_LIVING_ADDRESS_VALIDATION_ERRORS', field);
  },

  deleteLivingAddress({ commit }, livingAddress) {
    openConfirmDialog({
      title: i18n.t('confirm_living_address_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      livingAddressService.delete(livingAddress).then(() => {
        commit('DELETE_LIVING_ADDRESS', livingAddress);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('living_address_deleted'));
      });
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
